<template>
  <!-- Proyectos con riesgo contractual (***): matriz Impacto × Probabilidad. Cada fila se despliega
       (chevron) y muestra la Revisión contractual completa del proyecto (alcance + hitos + riesgos),
       con el mismo diseño del tab Dashboard (se carga al vuelo del endpoint del proyecto). -->
  <summary-card :title="$t('prm_portfolio_risk_title')" :subtitle="headerNote" :mode="mode">
    <template #extra>
      <span class="rk-scores">
        <span class="rk-scores-lbl">{{ $t('prm_portfolio_risk_score') }}:</span>
        <button v-for="lv in levels" :key="lv" class="rk-chip" :class="{ active: scoreFilter === lv }"
          @click="scoreFilter = scoreFilter === lv ? 'all' : lv">
          <span class="rk-dot" :style="{ background: SEM[lv] }"></span>
          {{ $t('prm_portfolio_col_' + lv) }} <span class="rk-cnt">({{ countBy(lv) }})</span>
        </button>
      </span>
    </template>

    <div class="rk-wrap">
      <table class="rk">
        <thead>
          <tr>
            <th class="ch"></th>
            <th class="left">{{ $t('prm_portfolio_col_project') }}<source-chip v-if="mode" source="admin"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_col_group') }}<source-chip v-if="mode" source="admin"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_col_client') }}<source-chip v-if="mode" source="resumen"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_col_pm') }}<source-chip v-if="mode" source="prm"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_risk_situation') }}<source-chip v-if="mode" source="calc"></source-chip></th>
            <th class="left">{{ $t('prm_portfolio_risk_criterion') }}<source-chip v-if="mode" source="resumen"></source-chip></th>
            <th class="center">{{ $t('prm_portfolio_risk_impact') }}<source-chip v-if="mode" source="resumen"></source-chip></th>
            <th class="center">{{ $t('prm_portfolio_risk_probability') }}<source-chip v-if="mode" source="resumen"></source-chip></th>
            <th class="center">{{ $t('prm_portfolio_risk_score') }}<source-chip v-if="mode" source="calc"></source-chip></th>
            <th class="center">{{ $t('prm_portfolio_risk_semaphore') }}<source-chip v-if="mode" source="calc"></source-chip></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in displayRows" :key="item.key"
            :class="item.type === 'exp' ? 'exp' : 'rrow'"
            :role="item.type === 'row' ? 'button' : null" :tabindex="item.type === 'row' ? 0 : null"
            :aria-expanded="item.type === 'row' ? isOpen(item.r) : null"
            :style="item.type === 'row' ? { boxShadow: 'inset 3px 0 0 0 ' + SEM[level(item.r)] } : null"
            @click="item.type === 'row' && toggle(item.r)"
            @keydown.enter="item.type === 'row' && toggle(item.r)"
            @keydown.space="item.type === 'row' && onSpace($event, item.r)">
            <template v-if="item.type === 'row'">
              <td class="ch"><b-icon pack="fas" :icon="isOpen(item.r) ? 'chevron-down' : 'chevron-right'" size="is-small"></b-icon></td>
              <td class="left proj">
                <span class="p-code" :style="{ color: item.r.groupColor }">{{ item.r.code || '-' }}</span>
                <span class="p-name" v-tip="item.r.name">{{ item.r.name }}</span>
              </td>
              <td class="left"><group-tag :group="item.r.group" :color="item.r.groupColor"></group-tag></td>
              <td class="left ptrunc" v-tip="item.r.client">{{ item.r.client || '-' }}</td>
              <td class="left ptrunc">{{ item.r.pm || '-' }}</td>
              <td class="left">
                <span class="sit" :style="sitStyle(item.r)">
                  <span class="sit-dot" :style="{ background: SEM[level(item.r)] }"></span>{{ $t('prm_portfolio_risk_crit_' + level(item.r)) }}
                </span>
              </td>
              <td class="left crit">{{ $t('prm_portfolio_risk_reason_' + level(item.r)) }}</td>
              <td class="center num">{{ item.r.riskImpact || '-' }}</td>
              <td class="center num">{{ item.r.riskProbability || '-' }}</td>
              <td class="center">
                <span class="score"><span class="sdot" :style="{ background: SEM[level(item.r)] }"></span><b :style="{ color: SEM[level(item.r)] }">{{ item.r.riskScore }}</b></span>
              </td>
              <td class="center"><sem-dot :state="level(item.r)" :title="$t('prm_portfolio_col_' + level(item.r))"></sem-dot></td>
            </template>
            <template v-else>
              <td colspan="11" class="exp-cell" :style="{ boxShadow: 'inset 3px 0 0 0 ' + SEM[level(item.r)] }">
                <div v-if="detail(item.r).loading" class="exp-loading"><scale-loader color="#3d6b9a" height="26px"></scale-loader></div>
                <div v-else-if="detail(item.r).error" class="exp-empty">{{ $t('error_retrieving_data') }}</div>
                <div v-else-if="detail(item.r).doc" class="exp-box">
                  <div class="exp-head">
                    <span class="exp-title">{{ $t('prm_portfolio_risk_review') }} · {{ item.r.name }}</span>
                    <span class="exp-sub">{{ counts(item.r) }}</span>
                    <button class="exp-open" @click.stop="$emit('open', item.r)">{{ $t('prm_portfolio_open_dashboard') }}</button>
                  </div>
                  <div class="exp-cards">
                    <scope-card :scope="detail(item.r).dashboard.scope" :mode="false" @save="(v) => saveScope(item.r, v)"></scope-card>
                    <contract-review-card :doc="detail(item.r).doc" :penalty-total="penaltyOf(item.r)" :mode="false"></contract-review-card>
                  </div>
                </div>
              </td>
            </template>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="11" class="empty">{{ $t('prm_portfolio_risk_empty') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Leyenda desplegable: nivel de riesgo contractual y su criterio. -->
    <legend-footer>
      <div class="rk-leg">
        <div v-for="lv in levels" :key="lv" class="rk-leg-row">
          <span class="rk-leg-head"><span class="rk-dot" :style="{ background: SEM[lv] }"></span>{{ $t('prm_portfolio_risk_level_' + lv) }}</span>
          <span class="rk-leg-txt">{{ $t('prm_portfolio_risk_reason_' + lv) }}</span>
        </div>
      </div>
    </legend-footer>
  </summary-card>
</template>

<script>
  import axios from 'axios'
  import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
  import SummaryCard from '../summary/summary-card.vue'
  import SourceChip from '../summary/source-chip.vue'
  import SemDot from '../dashboard/sem-dot.vue'
  import ScopeCard from '../dashboard/scope-card.vue'
  import ContractReviewCard from '../dashboard/contract-review-card.vue'
  import GroupTag from './group-tag.vue'
  import LegendFooter from './legend-footer.vue'
  import { tip } from '../shared/tip'
  import { SEM } from '../dashboard/dashboard-model'
  import { penaltyTotal as calcPenalty } from '../summary/summary-model'
  import { riskLevel } from './portfolio-model'

  export default {
    name: 'PrmPortfolioContractualRisk',

    components: { ScaleLoader, SummaryCard, SourceChip, SemDot, ScopeCard, ContractReviewCard, GroupTag, LegendFooter },

    directives: { tip },

    props: {
      allRows: { type: Array, default: () => [] },
      mode: { type: Boolean, default: false },
    },

    data: () => ({
      SEM,
      scoreFilter: 'all',
      levels: ['green', 'amber', 'red'],
      expanded: [],   // ids desplegados
      loaded: {},     // detalle por proyecto (doc/dashboard/sources), cargado al vuelo
    }),

    computed: {
      // Con filtro: ese nivel; sin filtro: los que tienen riesgo (ámbar/rojo), ordenados por score desc.
      rows () {
        const base = this.scoreFilter === 'all'
          ? this.allRows.filter((r) => this.level(r) !== 'green')
          : this.allRows.filter((r) => this.level(r) === this.scoreFilter)
        return base.slice().sort((a, b) => b.riskScore - a.riskScore)
      },
      noRisk () { return this.allRows.filter((r) => this.level(r) === 'green').length },
      // Filas + filas de desglose intercaladas (evita <template v-for> con key en el hijo).
      displayRows () {
        const out = []
        for (const r of this.rows) {
          out.push({ type: 'row', key: 'r' + r.id, r })
          if (this.isOpen(r)) out.push({ type: 'exp', key: 'e' + r.id, r })
        }
        return out
      },
      headerNote () {
        return this.scoreFilter === 'all'
          ? `${this.$t('prm_portfolio_risk_subtitle')} · ${this.rows.length} ${this.$t('prm_portfolio_projects_word')} · ${this.noRisk} ${this.$t('prm_portfolio_risk_none')}`
          : `${this.rows.length} ${this.$t('prm_portfolio_projects_word')}`
      },
    },

    methods: {
      level (r) { return riskLevel(r.riskScore) },
      countBy (lv) { return this.allRows.filter((r) => this.level(r) === lv).length },
      sitStyle (r) {
        const c = SEM[this.level(r)]
        return { background: c + '14', color: c, borderColor: c + '55' }
      },
      isOpen (r) { return this.expanded.includes(r.id) },
      toggle (r) {
        const i = this.expanded.indexOf(r.id)
        if (i === -1) {
          this.expanded.push(r.id)
          if (!this.loaded[r.id]) this.fetchDetail(r)
        } else {
          this.expanded.splice(i, 1)
        }
      },
      onSpace (e, r) { e.preventDefault(); this.toggle(r) },

      // Carga la Revisión contractual del proyecto (doc + dashboard) del endpoint del Dashboard.
      // Reasignamos el mapa (en vez de mutar) para mantener la reactividad sin $set.
      setDetail (id, value) { this.loaded = { ...this.loaded, [id]: value } },
      fetchDetail (r) {
        this.setDetail(r.id, { loading: true })
        axios.get(`/api/v2/project/${r.id}/dashboard-prm/dashboard`)
          .then(({ data }) => {
            const d = data.data
            this.setDetail(r.id, { doc: d.doc, dashboard: d.dashboard, sources: d.sources })
          })
          .catch(() => this.setDetail(r.id, { error: true }))
      },
      detail (r) { return this.loaded[r.id] || {} },
      counts (r) {
        const d = this.detail(r)
        const s03 = (d.doc && d.doc.s03) || {}
        const s05 = (d.doc && d.doc.s05) || []
        return this.$t('prm_portfolio_risk_counts', [(s03.contractMilestones || []).length, (s03.penaltyMilestones || []).length, s05.length])
      },
      penaltyOf (r) {
        const d = this.detail(r)
        if (!d.doc) return 0
        const s03 = d.doc.s03 || {}
        const total = (d.sources && d.sources.prm && d.sources.prm.totalAmount) || 0
        return calcPenalty(s03.penaltyMilestones, total, (d.doc.meta || {}).penaltyCapPct)
      },
      saveScope (r, v) {
        axios.put(`/api/v2/project/${r.id}/dashboard-prm/dashboard/field`, { path: 'scope', value: v })
          .then(() => {
            const cur = this.loaded[r.id]
            if (cur && cur.dashboard) this.setDetail(r.id, { ...cur, dashboard: { ...cur.dashboard, scope: v } })
          })
          .catch(() => this.$notify.error(this.$t('error_saving_data')))
      },
    },
  }
</script>

<style lang="scss" scoped>
  .rk-scores { display: inline-flex; align-items: center; gap: 5px; margin-left: auto; flex-wrap: wrap; }
  .rk-scores-lbl { font-size: 10px; color: #7a8798; font-weight: 400; text-transform: none; }
  .rk-chip {
    display: inline-flex; align-items: center; gap: 5px; border: 1px solid #d4dae3; background: #fff;
    color: #3f4a5c; font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 5px; cursor: pointer; text-transform: none;
  }
  .rk-chip.active { border-color: #3a6bc8; box-shadow: 0 0 0 1px #3a6bc8; }
  .rk-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .rk-cnt { color: #9aa4b2; font-variant-numeric: tabular-nums; }

  .rk-wrap { overflow-x: auto; }
  .rk { width: 100%; min-width: 1120px; border-collapse: collapse; }
  .rk thead th {
    background: #3d6b9a; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .03em; padding: 5px 8px; white-space: nowrap;
  }
  .rk thead th.left { text-align: left; }
  .rk thead th.center { text-align: center; }
  .rk thead th.ch { width: 24px; }
  .rk tbody td { border-bottom: 1px solid #eef0f3; padding: 5px 8px; font-size: 11px; color: #1e3046; white-space: nowrap; vertical-align: middle; }
  .rk tbody td.left { text-align: left; }
  .rk tbody td.center { text-align: center; }
  .rk tbody td.num { font-variant-numeric: tabular-nums; }
  .rk tbody td.ch { text-align: center; color: #6b7787; }
  .rrow { cursor: pointer; }
  .rrow:hover td { background: #eef2f7; }
  .rrow:focus { outline: none; }

  .proj { max-width: 230px; }
  .p-code { font-family: ui-monospace, monospace; font-size: 10px; font-weight: 700; margin-right: 6px; }
  .p-name { font-weight: 600; }
  .ptrunc { max-width: 140px; overflow: hidden; text-overflow: ellipsis; }

  // Situación del proyecto: chip con punto + texto, coloreado por nivel.
  .sit {
    display: inline-flex; align-items: center; gap: 5px; border: 1px solid; border-radius: 999px;
    padding: 1px 8px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; white-space: nowrap;
  }
  .sit-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .crit { color: #5a6577; white-space: normal; min-width: 220px; line-height: 1.3; }

  // Score: punto de color + número coloreado (sin círculo alrededor).
  .score { display: inline-flex; align-items: center; justify-content: center; gap: 5px; }
  .sdot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .score b { font-weight: 700; font-variant-numeric: tabular-nums; }

  // Fila desplegada: Revisión contractual completa (mismo diseño del Dashboard). Fondo suave
  // con acento de color a la izquierda y padding equilibrado a ambos lados.
  .exp td.exp-cell { background: #f6f8fb; padding: 8px 16px 14px; white-space: normal; }
  .exp-loading { display: flex; justify-content: center; padding: 16px 0; }
  .exp-empty { padding: 12px; font-size: 11px; color: #7a8798; }
  .exp-box { padding: 4px 0; }
  .exp-head { display: flex; align-items: center; gap: 10px; padding: 6px 2px; }
  .exp-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #1e3046; }
  .exp-sub { font-size: 10px; color: #9aa4b2; }
  .exp-open {
    margin-left: auto; border: 1px solid #b9c8da; background: #fff; color: #3d6b9a;
    font-size: 10px; font-weight: 600; padding: 2px 10px; border-radius: 6px; cursor: pointer;
  }
  .exp-open:hover { background: #eef4fa; }
  .exp-cards { display: flex; flex-direction: column; gap: 10px; }

  .rk-leg { display: flex; flex-direction: column; gap: 5px; }
  .rk-leg-row { display: grid; grid-template-columns: 220px 1fr; gap: 10px; align-items: baseline; }
  .rk-leg-head { display: inline-flex; align-items: center; gap: 6px; font-size: 10px; font-weight: 700; color: #1e3046; white-space: nowrap; }
  .rk-leg-txt { font-size: 10px; color: #6b7787; line-height: 1.35; }

  .empty { text-align: center; color: #9aa4b2; padding: 18px; font-size: 11px; }
</style>
